import { DateTime } from 'luxon';
import XhrRequestHandler from '../../lib/XhrRequestHandler';
import { AgrupadorCorporacionLiquidadoraTipo } from '../agrupador_corporacion_liquidadora/AgrupadorCorporacionLiquidadoraSystem';

/* =========================
   RESPONSES
========================= */

// Respuesta genérica del backend
export interface ApiResponse<T> {
  statusCode: number;
  statusMsg: string;
  statusDetail: string;
  debugInfo: unknown;
  httpCode: number | null;
  attributes: unknown;
  data: T;
  segment: unknown;
}

export interface LiquidacionAsincronaV2ListResponse extends ApiResponse<LiquidacionAsincronaV2[]> {}

export interface LiquidacionAsincronaV2CreateResponse extends ApiResponse<LiquidacionAsincronaV2> {}

export interface LiquidacionAsincronaV2AceptarResponse extends ApiResponse<LiquidacionAsincronaV2> {}

export interface LiquidacionAsincronaV2EliminarResponse extends ApiResponse<LiquidacionAsincronaV2> {}

export interface LiquidacionAsincronaDetalleListResponse extends ApiResponse<LiquidacionAsincronaDetalle[]> {}

export interface LiquidacionAsincronaV2ExistePeriodoResponse extends ApiResponse<{ existe: boolean }> {}

export interface LiquidacionAsincronaV2GetByIdResponse extends ApiResponse<LiquidacionAsincronaV2Details> {}

export interface LiquidacionAsincronaV2ExcelCorregidoResponse extends ApiResponse<ExcelData> {}

export interface LiquidacionAsincronaV2Details {
  liquidacion: LiquidacionAsincronaV2;
  detalle: LiquidacionAsincronaDetalle[];
}

export interface ExcelData {
  columns: string[];
  rows: { [key: string]: string }[];
}

interface LiqasincdetData {
  landing: Landing[];
  [key: string]: unknown;
}

interface Landing {
  ajeFilms: string;
  [key: string]: string;
}

/* =========================
   ENTITIES
========================= */

export interface LiquidacionAsincronaV2 {
  lidasinc_id: string;
  lidasinc_periodo: string;
  lidasinc_estado: LiquidacionAsincronaEstado;
  lidasinc_fecha_creacion: string;
  lidasinc_fecha_ejecucion: string | null;
  lidasinc_fecha_actualizacion: string | null;
  lidasinc_solicitud_colid: string;
  lidasinc_eliminado: boolean;
  lidasinc_activo: boolean;
  lidasinc_agrcorliqid: string;
  lidasinc_tipo_liquidacion: string | null;
  lidasinc_col_nombres_completos: string | null;
  lidasinc_importe_total: number | null;
  lidasinc_aceptado_colid: string | null;
  lidasinc_eliminado_colid: string | null;
  lidasinc_aceptado_col_nombres_completos: string | null;
  lidasinc_eliminado_col_nombres_completos: string | null;
}

export interface LiquidacionAsincronaDetalle {
  liqasincdet_id: string;
  liqasincdet_liqasincid: string;
  liqasincdet_data: LiqasincdetData;
  liqasincdet_activo: boolean;
  liqasincdet_eliminado: boolean;
  liqasincdet_fecha_creacion: string;
  liqasincdet_fecha_actualizacion: string | null;
}

export interface LiquidacionAsincronaV2ExistePeriodoPayload {
  lidasinc_periodo: string;
  lidasinc_agrcorliqid: number | string;
}

export interface RemuneracionActividad {
  periodo:              string;
  col_id:               string;
  col_sap_id:           string;
  col_documento_numero: string;
  col_tipo_documento:   null;
  col_nombre_completo:  string;
  col_email:            string;
  col_fecha_alta:       Date;
  col_fecha_baja:       null;
  col_baja_motivo:      string;
  act_id:               string;
  act_nombre:           string;
  act_inicio:           string;
  col_horas:            string;
}


/* =========================
   ENUMS
========================= */

export enum LiquidacionAsincronaEstado {
  REGISTRADO = 'REGISTRADO',
  PENDIENTE = 'PENDIENTE',
  PRELIQUIDADO = 'PRELIQUIDADO',
  ACEPTADO = 'ACEPTADO',
  ELIMINADO = 'ELIMINADO',
  REPROCESAR = 'REPROCESAR',
  PROCESANDO = 'PROCESANDO',
}

/* =========================
   PAYLOADS
========================= */

export interface LiquidacionAsincronaV2CreatePayload {
  liquidacion: {
    lidasinc_periodo: string;
    lidasinc_agrcorliqid: number | string;
    lidasinc_solicitud_colid: number | string;
  };
  detalle: {
    liqasincdet_data: Record<string, any>;
  };
}
/* =========================
   SYSTEM
========================= */

export class LiquidacionAsincronaV2System {
  private readonly base = '/liquidacion-asincrona';
  private readonly detalleBase = '/liquidacion-asincrona-detalle';

  constructor(private requestHandler: XhrRequestHandler) {}

  /**
   * Lista liquidaciones asincrónicas
   */
  async list() {
    return this.requestHandler.get<LiquidacionAsincronaV2ListResponse>(`${this.base}/list`);
  }

  getBaseUrlForDatagridDirect() {
    return this.requestHandler.requestUrl(`${this.base}/datagrid-direct-aje`);
  }

  /**
   * Crea una liquidación asincrónica con su detalle
   */
  async create(payload: LiquidacionAsincronaV2CreatePayload) {
    return this.requestHandler.post<LiquidacionAsincronaV2CreateResponse>(`${this.base}/create`, payload);
  }

  /**
   * Lista el detalle de una liquidación asincrónica
   */
  async listDetalle(liqasincid: number) {
    return this.requestHandler.get<LiquidacionAsincronaDetalleListResponse>(`${this.detalleBase}/list/${liqasincid}`);
  }

  async getById(id: string) {
    return this.requestHandler.get<LiquidacionAsincronaV2GetByIdResponse>(`${this.base}/get-by-id/${id}`);
  }

  /**
   * Valida si ya existe una liquidación en el período
   */
  async existsByPeriodo(payload: LiquidacionAsincronaV2ExistePeriodoPayload) {
    return this.requestHandler.post<LiquidacionAsincronaV2ExistePeriodoResponse>(`${this.base}/exists-periodo`, payload);
  }

  async aceptar(id: string) {
    return this.requestHandler.post<LiquidacionAsincronaV2AceptarResponse>(`${this.base}/aceptar/${id}`);
  }

  async eliminar(id: string) {
    return this.requestHandler.post<LiquidacionAsincronaV2EliminarResponse>(`${this.base}/eliminar/${id}`);
  }

  async getReporteRevision(id: string) {
    return this.requestHandler.get<ExcelData>(`${this.base}/reporte-revision/${id}`);
  }

  async actualizarReporte(id: string, rows: any[], importeTotal: string) {
    return this.requestHandler.post(`${this.base}/actualizar-reporte/${id}`, { rows, importeTotal });
  }

  async getDetallePreliquidacionAje(id: string) {
    return this.requestHandler.get<LiquidacionAsincronaV2ExcelCorregidoResponse>(`${this.base}/reporte-liquidado/${id}`);
  }

  async downloadLiquidacion(id: string): Promise<Blob> {
    const url = `/liquidacion-asincrona/download-liquidacion/${id}`;
    return this.requestHandler.getBlob(url);
  }

  async downloadInsumos(id: string): Promise<Blob> {
    const url = `/liquidacion-asincrona/download-insumos/${id}`;
    return this.requestHandler.getBlob(url);
  }

  /* 
   * Si retorna un array vacío, Significa que si hay remuneración para todas las actividades
   * Si retorna un array con datos, Significa que no hay remuneración para las actividades listadas 
   */
  public async getRemunInActividad(period: DateTime, tipo: AgrupadorCorporacionLiquidadoraTipo) {
    const data = await this.requestHandler.get<RemuneracionActividad[]>(
      `${this.base}/remuneracion-por-actividad/${period.toFormat("yyyy")}/${period.toFormat("LL")}/${tipo}`,
    );
    return data
  }

  public async getActividadInRange(period: DateTime, tipo: AgrupadorCorporacionLiquidadoraTipo) {
    const data = await this.requestHandler.get<RemuneracionActividad[]>(
      `${this.base}/actividades-existentes/${period.toFormat("yyyy")}/${period.toFormat("LL")}/${tipo}`,
    );
    return data
  }

}
